import Box from "@mui/joy/Box";
import CircularProgress from "@mui/joy/CircularProgress";
import React, { ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { BundleContext } from "../../../App";
import { Annotation, Fact } from "../../../models/fact";
import { Note, NoteId } from "../../../models/note";
import BackdropLoader from "../../../shared/components/feedback/BackdropLoader";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import { NoteQuartzClientContext } from "../../../shared/services/api/contexts";
import { NoteContext } from "../contexts";

interface NoteProviderProps {
  note_id: NoteId;
  children: ReactNode;
}

export interface Label {
  annotation: Annotation;
  color: string;
  fact?: Fact;
  domain?: string;
}

export const NoteProvider: React.FC<NoteProviderProps> = ({ note_id, children }) => {
  // Contexts
  const { showSnackbar } = useSnackbar();
  const noteQuartzClient = React.useContext(NoteQuartzClientContext);
  const [bundleId] = React.useContext(BundleContext);
  const navigate = useNavigate();

  // States
  const [note, setNoteValue] = React.useState<Note | null>(null);
  const [selectedFact, setSelectedFactValue] = React.useState<Fact | null>(null);
  const [selectedLabels, setSelectedLabelsValue] = React.useState<Label[]>([]);
  const [hoveredFact, setHoveredFact] = React.useState<Fact | null>(null);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [loading, setLoading] = React.useState<boolean>(true);

  const handleReRunNLP = useCallback(async () => {
    if (!note) {
      console.error("No note provided for validation or skipping.");
      return;
    }
    setLoading(true);
    try {
      const new_note = await noteQuartzClient.runNLP(note.note_id);
      setNoteValue(new_note);
    } catch (error) {
      console.error("Error while running NLP:", error);
      showSnackbar({ text: "Error while running NLP", color: "danger" });
    } finally {
      setLoading(false);
    }
  }, [note, noteQuartzClient, showSnackbar]);

  const navigateToNote = useCallback(
    (note: Note) => {
      navigate(`/note/${note.note_id}`);
      setNoteValue(note);
    },
    [navigate]
  );

  const handleSkipNote = useCallback(
    async (noteToSkip: Note) => {
      if (!bundleId) return;

      const newNote = await noteQuartzClient.skipNote(noteToSkip.note_id);
      if (newNote) {
        navigateToNote(newNote);
      } else if (noteToSkip.person_id) {
        navigate(`/patient/${noteToSkip.person_id}`);
      } else {
        navigate("/notes");
      }
    },
    [bundleId, noteQuartzClient, navigate, navigateToNote]
  );

  const handleValidateNote = useCallback(
    async (noteToValidate: Note, next: boolean) => {
      const updatedNote = await noteQuartzClient.validateNoteById(noteToValidate.note_id);

      if (updatedNote && next && bundleId) {
        const nextNote = await noteQuartzClient.getNextNote(updatedNote.note_id);
        if (nextNote) {
          navigateToNote(nextNote);
        } else {
          navigate(`/patient/${updatedNote.person_id}`);
        }
      } else {
        setNoteValue(updatedNote);
      }
    },
    [noteQuartzClient, bundleId, navigate, navigateToNote]
  );

  const handleValidate = useCallback(
    async (next = false, skip = false) => {
      if (!note) {
        console.error("No note provided for validation or skipping.");
        return;
      }

      try {
        setLoading(true);
        if (skip) {
          await handleSkipNote(note);
        } else {
          await handleValidateNote(note, next);
        }
      } catch (e: any) {
        showSnackbar({ text: e.message, color: "danger" });
      } finally {
        setLoading(false);
      }
    },
    [note, handleSkipNote, handleValidateNote, showSnackbar]
  );

  const setNote = useCallback(
    async (newNote: Note) => {
      try {
        const updatedNote = await noteQuartzClient.updateNoteById(note_id, newNote);
        setNoteValue(updatedNote);
      } catch (error) {
        console.error("Failed to update note:", error);
      }
    },
    [note_id, noteQuartzClient]
  );

  const setSelectedFact = React.useCallback((fact: Fact) => {
    setSelectedFactValue(fact);
    setSelectedLabelsValue([]);
  }, []);

  const toggleSelectedLabel = React.useCallback(
    (label: Label, additive: boolean = true) => {
      if (note) {
        setSelectedFactValue(null);
        if (additive) {
          setSelectedLabelsValue((prevLabels: Label[]) => {
            const exists = prevLabels.some(
              (l: Label) =>
                l.annotation.offset === label.annotation.offset &&
                l.annotation.element_index === label.annotation.element_index
            );
            return exists
              ? prevLabels.filter(
                  (l: Label) =>
                    !(
                      l.annotation.offset === label.annotation.offset &&
                      l.annotation.element_index === label.annotation.element_index
                    )
                )
              : [...prevLabels, label];
          });
        } else {
          setSelectedLabelsValue([label]);
        }
      }
    },
    [note]
  );

  const setDisplayAllFacts = React.useCallback(() => {
    if (note) {
      setSelectedFactValue(null);
      setSelectedLabelsValue([]);
    }
  }, [note]);

  React.useEffect(() => {
    const fetchNote = async () => {
      try {
        const fetchedNote = await noteQuartzClient.getNoteById(note_id);
        setNoteValue(fetchedNote);
      } catch (error) {
        console.error("Failed to fetch note:", error);
        navigate("/notes");
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [note_id, noteQuartzClient, navigate]);

  const isAnnotationMatchingLabel = (annotation: Annotation, label: Label): boolean => {
    return (
      label.annotation.offset === annotation.offset &&
      label.annotation.element_index === annotation.element_index
    );
  };

  const isFactMatchingSelectedLabels = useCallback(
    (fact: Fact): boolean => {
      return fact.source.some((annotation: Annotation) =>
        selectedLabels.some((label) => isAnnotationMatchingLabel(annotation, label))
      );
    },
    [selectedLabels]
  );

  const displayedFacts = React.useMemo(() => {
    if (!note) return [];
    if (selectedFact) {
      return note.facts.filter((fact: Fact) => fact.fact_id === selectedFact.fact_id);
    }
    if (selectedLabels.length > 0) {
      return note.facts.filter(isFactMatchingSelectedLabels);
    }
    return note.facts;
  }, [note, selectedFact, selectedLabels, isFactMatchingSelectedLabels]);

  const value = useMemo(
    () => ({
      note,
      setNote,
      handleValidate,
      handleReRunNLP,
      loading,
      selectedLabels,
      toggleSelectedLabel,
      selectedFact,
      setSelectedFact,
      displayedFacts,
      hoveredFact,
      setHoveredFact,
      setDisplayAllFacts,
      selectedTab,
      setSelectedTab,
    }),
    [
      note,
      setNote,
      handleValidate,
      handleReRunNLP,
      loading,
      selectedLabels,
      toggleSelectedLabel,
      selectedFact,
      setSelectedFact,
      displayedFacts,
      hoveredFact,
      setHoveredFact,
      setDisplayAllFacts,
      selectedTab,
      setSelectedTab,
    ]
  );

  if (loading) {
    return (
      <div>
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress variant="solid" />
        </Box>
      </div>
    );
  }

  if (!note) {
    navigate("/notes");
    return null;
  }

  return (
    <NoteContext.Provider value={value}>
      <BackdropLoader loading={loading} text="Loading...">
        {children}
      </BackdropLoader>
    </NoteContext.Provider>
  );
};
